import { Paper, SxProps, Theme } from "@mui/material";
import {
  DataGrid,
  DataGridProps,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { CustomPagination } from "./CustomPagination";

interface ICustomDataGridProps<T> {
  columns: GridColDef<T>[];
  rows: T[];
  rowCount: number;
  loading?: boolean;
  onRowClick?: (params: GridRowParams<T>) => void;
  getRowId?: (row: T) => string | number;
  pageSizeOptions?: number[];
  paginationMode?: "client" | "server";
  paginationModel?: { page: number; pageSize: number };
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  // sorting props
  sortingMode?: "client" | "server";
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  autoHeight?: boolean;
  checkboxSelection?: boolean;
  onSelectionModelChange?: (selectionModel: GridRowSelectionModel) => void;
  selectionModel?: GridRowSelectionModel;
  disableSelectionOnClick?: boolean;
  getRowClassName?: (params: GridRowParams<T>) => string;
  componentsProps?: object;
  // Wrapper Paper sx
  sx?: SxProps<Theme>;
  // DataGrid-level sx override to merge with default styles
  dataGridSx?: (theme: Theme) => Record<string, unknown>;
  // Pass-through to MUI DataGrid
  slots?: Partial<DataGridProps<T>["slots"]>;
  slotProps?: Record<string, unknown>;
  density?: "compact" | "standard" | "comfortable";
  disableColumnMenu?: boolean;
  filterMode?: "client" | "server";
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
  hideFooterSelectedRowCount?: boolean;
  hideFooter?: boolean;
  visibleColumns?: string[];
}

const DATAGRID_STYLES = (theme: Theme) => {
  const { palette } = theme;
  const isLight = palette.mode === "light";
  const headerBg = isLight ? palette.primary.lighter : palette.primary.light;
  const headerFg = isLight ? palette.text.primary : palette.common.white;

  return {
    "--DataGrid-containerBackground": headerBg,
    "--DataGrid-headerBackground": headerBg,
    border: 0,
    borderRadius: 0,
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
      outline: "none",
    },
    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
      {
        outline: "none",
      },

    "& .MuiDataGrid-cell--editing": {
      outline: "none",
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: `${headerBg} !important`,
      backgroundImage: "none !important",
      color: headerFg,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
    "& .MuiDataGrid-columnHeadersInner, & .MuiDataGrid-main, & .MuiDataGrid-withBorderColor, & .MuiDataGrid-columnHeader":
      {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      },
    "& .MuiDataGrid-columnHeader": {
      "&:first-of-type": { borderTopLeftRadius: 0 },
      "&:last-of-type": { borderTopRightRadius: 0 },
    },
    "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitleContainer": {
      backgroundColor: `${headerBg} !important`,
      backgroundImage: "none !important",
      color: headerFg,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 700,
      color: "inherit",
    },
    "& .MuiDataGrid-columnHeaders .MuiSvgIcon-root, & .MuiDataGrid-columnHeaders .MuiCheckbox-root, & .MuiDataGrid-columnHeaders .MuiCheckbox-root.Mui-checked":
      {
        color: "inherit",
      },
    "& .MuiDataGrid-columnSeparator": {
      color: isLight ? palette.text.secondary : palette.grey[700],
      opacity: 0.4,
    },
    "& .MuiDataGrid-row": {
      "&:hover": {
        backgroundColor: palette.action.hover,
      },
      "&.Mui-selected": {
        backgroundColor: palette.action.selected,
        "&:hover": {
          backgroundColor: palette.action.hover,
        },
      },
    },
  } as const;
};

export const CustomDataGrid = <T extends object>({
  columns,
  rows,
  rowCount,
  loading = false,
  onRowClick,
  getRowId,
  pageSizeOptions = [10, 25, 50, 100],
  paginationMode = "server",
  paginationModel = { page: 0, pageSize: 10 },
  onPaginationModelChange = () => {},
  sortingMode,
  sortModel,
  onSortModelChange,
  autoHeight = true,
  checkboxSelection = false,
  onSelectionModelChange,
  selectionModel,
  disableSelectionOnClick = false,
  getRowClassName,
  componentsProps,
  sx = {},
  dataGridSx,
  slots,
  slotProps,
  density,
  disableColumnMenu,
  filterMode,
  filterModel,
  onFilterModelChange,
  hideFooterSelectedRowCount,
  hideFooter,
  visibleColumns,
}: ICustomDataGridProps<T>) => {
  const paginationModelSetter = (
    update:
      | ((prev: { page: number; pageSize: number }) => {
          page: number;
          pageSize: number;
        })
      | { page: number; pageSize: number }
  ) => {
    const nextModel =
      typeof update === "function"
        ? (
            update as (prev: { page: number; pageSize: number }) => {
              page: number;
              pageSize: number;
            }
          )(paginationModel)
        : update;
    onPaginationModelChange(nextModel);
  };

  const filteredColumns = columns.filter(
    (c) =>
      (visibleColumns as string[]).includes(c.field as string) ||
      c.field === "actions"
  );

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        overflow: "hidden",
        borderRadius: 0,
        ...sx,
      }}
    >
      <DataGrid
        rows={rows}
        columns={filteredColumns}
        loading={loading}
        onRowClick={onRowClick}
        getRowId={getRowId}
        rowCount={rowCount}
        paginationMode={paginationMode}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortingMode={sortingMode}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        pageSizeOptions={pageSizeOptions}
        autoHeight={autoHeight}
        checkboxSelection={checkboxSelection}
        onRowSelectionModelChange={onSelectionModelChange}
        rowSelectionModel={selectionModel}
        disableRowSelectionOnClick={disableSelectionOnClick}
        getRowClassName={getRowClassName}
        slots={{
          pagination:
            paginationMode === "server"
              ? () => (
                  <CustomPagination
                    totalRows={rowCount}
                    pageSizeOptions={pageSizeOptions}
                    paginationModel={paginationModel}
                    setPaginationModel={paginationModelSetter}
                  />
                )
              : undefined,
          ...slots,
        }}
        slotProps={{ ...componentsProps, ...slotProps }}
        density={density}
        disableColumnMenu={disableColumnMenu}
        filterMode={filterMode}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        hideFooterSelectedRowCount={hideFooterSelectedRowCount}
        hideFooter={hideFooter}
        sx={(theme) => ({
          ...DATAGRID_STYLES(theme),
          ...(dataGridSx ? dataGridSx(theme) : {}),
        })}
      />
    </Paper>
  );
};

export default CustomDataGrid;
